import type { TFunction } from 'i18next';
import { GitBranch, KeyRound, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types-administration';
import { formatDate } from '@/lib/intl';
import { useKeyedPendingWrite } from '@/lib/use-pending-write';
import { isStale, problemMessage, standingChanged } from '../api/problem';
import { samePlace, placeFromRef } from '../place/place';
import { useRoleText } from '../roles/role-text';
import { inheritedFrom } from '../ui/person-bits';

type RoleHolding = components['schemas']['RoleHolding'];
type Translate = TFunction<'administration'>;

type HoldingProps = {
  readonly holding: RoleHolding;
  readonly personName: string;
  readonly placeKind: 'tenants' | 'organizations';
  readonly placeId: string;
  readonly currentPlace: Parameters<typeof samePlace>[0];
  readonly onChanged: () => void;
  readonly onStanding: () => void;
};

/** Where a holding comes from: its inheritance, another place, or a direct grant, with its date. */
function holdingSource(
  t: Translate,
  holding: RoleHolding,
  currentPlace: HoldingProps['currentPlace'],
) {
  const heldPlace = placeFromRef(holding.place);
  const elsewhere = heldPlace !== null && !samePlace(heldPlace, currentPlace);
  const inherited = inheritedFrom(t, holding, 'sheet');
  if (inherited !== null) return inherited;
  return elsewhere
    ? t('person.holdingElsewhere', { place: holding.place.name })
    : t('person.holdingDirect', { date: formatDate(holding.grantedAt) });
}

/**
 * Revoking a role takes the derogated confirmation toast, without undo (FR-UX-5): it is reversed
 * by granting the role again, through the same panel that granted it.
 */
function useRevoke(props: HoldingProps, roleName: string) {
  const { holding, personName, placeKind, placeId, onChanged, onStanding } = props;
  const { t } = useTranslation('administration');
  const { isPending, run } = useKeyedPendingWrite();
  const revoke = () =>
    run(holding.assignmentId, async () => {
      const { error, response } = await apiClient.POST(
        '/v1/administration/{placeKind}/{placeId}/role-assignments/{assignmentId}/revocation',
        { params: { path: { placeKind, placeId, assignmentId: holding.assignmentId } }, body: {} },
      );
      if (!response.ok) {
        toast.error(problemMessage(t, error, response.status));
        if (standingChanged(error)) {
          onStanding();
        }
        if (isStale(error)) {
          onChanged();
        }
        return;
      }
      toast.success(t('person.revoked', { role: roleName, name: personName }));
      onChanged();
    });
  return { revoke, pending: isPending(holding.assignmentId) };
}

function HoldingTail({
  holding,
  roleName,
  revoke,
  pending,
}: {
  readonly holding: RoleHolding;
  readonly roleName: string;
  readonly revoke: () => unknown;
  readonly pending: boolean;
}) {
  const { t } = useTranslation('administration');
  if (holding.can.revoke) {
    return (
      <Button
        variant="ghost"
        size="icon"
        aria-label={t('person.revoke', { role: roleName })}
        disabled={pending}
        onClick={() => void revoke()}
      >
        <X />
      </Button>
    );
  }
  if (holding.via === 'direct') return null;
  return (
    <span className="text-muted-foreground px-1.5 text-[12px] font-semibold">
      {t('person.inherited')}
    </span>
  );
}

export function HoldingItem(props: HoldingProps) {
  const { holding, currentPlace } = props;
  const { t } = useTranslation('administration');
  const { name, description } = useRoleText();
  const roleName = name(holding.role);
  const desc = description(holding.role);
  const source = holdingSource(t, holding, currentPlace);
  const { revoke, pending } = useRevoke(props, roleName);

  return (
    <li className="person-holding">
      <span aria-hidden="true" className="person-holding-icon">
        {holding.via === 'direct' ? (
          <KeyRound className="size-4.5" />
        ) : (
          <GitBranch className="size-4.5" />
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-foreground text-[14.5px] font-semibold">{roleName}</span>
        {desc !== null ? <span className="text-muted-foreground text-[13px]">{desc}</span> : null}
        <span className="text-muted-foreground text-[12.5px]">{source}</span>
      </span>
      <HoldingTail holding={holding} roleName={roleName} revoke={revoke} pending={pending} />
    </li>
  );
}
